import "lib/@hooks/useEvent"
import { useEffect, useMemo, useRef, useState } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { CancelAsync, useIdleAsync } from "lib/@hooks/useIdleAsync"
import { cancelIdle, scheduleIdle } from "lib/schedule-idle-callback"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { getAuthoringScheduleRecommendations } from "../controller/get-authoring-schedule-recommendations"
import { resolveAuthoringRecommendationDraft } from "../lib/authoring-schedule-recommendations"
import { countAuthoringRecommendationDisplayItems } from "../lib/authoring-recommendation-groups"
import {
    buildAuthoringRecommendationDisplayIndex,
    patchAuthoringRecommendationDisplayItems,
} from "../lib/authoring-recommendation-edit-scopes"
import { buildAuthoringRecommendationDisplayItemsIdle } from "../lib/build-authoring-recommendation-display-items-idle"
import { DraftEdited, DraftStructureChanged } from "../schedule-editor-event-definitions.mjs"
import { useScheduleEditorIds } from "./use-schedule-editor-ids"
import { useScheduleEditorDraftStructureVersion } from "./use-schedule-editor-draft-structure-version"

export const EMPTY_AUTHORING_RECOMMENDATION_SUMMARY = {
    items: [],
    counts: {
        totalCount: 0,
        unappliedCount: 0,
        appliedCount: 0,
        readyCount: 0,
        conflictedCount: 0,
        choiceRequiredCount: 0,
    },
}

/**
 * @returns {{ summary: typeof EMPTY_AUTHORING_RECOMMENDATION_SUMMARY, refetchRecommendations: Function | undefined }}
 */
export function useAuthoringRecommendationSummaryFromContext() {
    const { authoringRecommendationSummary, refetchAuthoringRecommendations, authoringRecommendationsLoading } =
        useBoundContext()

    return {
        summary: authoringRecommendationSummary ?? EMPTY_AUTHORING_RECOMMENDATION_SUMMARY,
        refetchRecommendations: refetchAuthoringRecommendations,
        recommendationsLoading: !!authoringRecommendationsLoading,
    }
}

/**
 * Single idle rebuild owner for tab badge and nudge. Mount once via AuthoringRecommendationSummaryBound.
 *
 * Tier 1: idle full rebuild on structure / recommendations fetch.
 * Tier 2: scoped idle patch on field edits (conflict counts only).
 */
export function useAuthoringRecommendationSummary() {
    const { target, rawSchedule } = useBoundContext()
    const { projectId, scheduleId, draftId } = useScheduleEditorIds()
    const [recommendations, refetchRecommendations] = getAuthoringScheduleRecommendations.useResultsOnce.withRefetch(
        { projectId, scheduleId, sourceFilter: "all" },
        withInitialValue(IS_LOADING)
    )
    const draft = resolveAuthoringRecommendationDraft({ target, rawSchedule })
    const draftStructureVersion = useScheduleEditorDraftStructureVersion()

    const [summary, setSummary] = useState(EMPTY_AUTHORING_RECOMMENDATION_SUMMARY)
    const indexRef = useRef(null)
    const pendingScopesRef = useRef(new Set())
    const idleHandleRef = useRef(null)

    const rebuildKey = useMemo(buildRebuildKey, [draftStructureVersion, recommendations])
    const rebuildKeyId = useMemo(() => JSON.stringify(rebuildKey), [rebuildKey])

    const idleSummary = useIdleAsync(buildSummary, EMPTY_AUTHORING_RECOMMENDATION_SUMMARY, rebuildKeyId)

    useEffect(cancelPendingEditPatchOnRebuild, [rebuildKeyId])
    useEffect(syncSummaryFromIdleSummary, [idleSummary, rebuildKeyId, recommendations])
    DraftEdited(draftId).useEvent(handleDraftEdited)
    DraftStructureChanged(draftId).useEvent(cancelPendingEditPatch)

    return { summary, refetchRecommendations, recommendationsLoading: recommendations === IS_LOADING }

    function buildRebuildKey() {
        return {
            draftStructureVersion,
            recommendationsKey: buildRecommendationsKey(recommendations),
        }
    }

    async function buildSummary(yieldIdle) {
        if (recommendations === IS_LOADING) return CancelAsync

        const items = await buildAuthoringRecommendationDisplayItemsIdle({
            recommendations: recommendations ?? [],
            draft,
            sourceFilter: "all",
            selectedRecommendationIdsByGroupKey: {},
            yieldIdle,
        })

        return {
            items,
            counts: countAuthoringRecommendationDisplayItems({ items, draft }),
        }
    }

    function syncSummaryFromIdleSummary() {
        if (recommendations === IS_LOADING) return

        cancelPendingEditPatch()
        pendingScopesRef.current.clear()
        indexRef.current = buildAuthoringRecommendationDisplayIndex(recommendations ?? [], idleSummary.items ?? [])
        setSummary(idleSummary)
    }

    function cancelPendingEditPatchOnRebuild() {
        cancelPendingEditPatch()
    }

    function handleDraftEdited(payload) {
        const scopes = Array.isArray(payload?.scopes) ? payload.scopes : []
        if (!scopes.length) return

        for (const scope of scopes) pendingScopesRef.current.add(scope)
        queueIdlePatch()
    }

    function queueIdlePatch() {
        cancelIdle(idleHandleRef.current)
        idleHandleRef.current = scheduleIdle(runIdlePatch)
    }

    function runIdlePatch() {
        idleHandleRef.current = null

        const scopes = [...pendingScopesRef.current]
        pendingScopesRef.current.clear()

        if (!scopes.length || !indexRef.current || !draft) return

        setSummary((current) => {
            const items = patchAuthoringRecommendationDisplayItems({
                items: current.items,
                draft,
                index: indexRef.current,
                scopes,
                selectedRecommendationIdsByGroupKey: {},
            })

            return {
                items,
                counts: countAuthoringRecommendationDisplayItems({ items, draft }),
            }
        })
    }

    function cancelPendingEditPatch() {
        cancelIdle(idleHandleRef.current)
        idleHandleRef.current = null
        pendingScopesRef.current.clear()
    }
}

function buildRecommendationsKey(recommendations) {
    if (recommendations === IS_LOADING) return "loading"
    if (!Array.isArray(recommendations)) return "empty"
    return recommendations
        .map((item) => item?.id)
        .filter(Boolean)
        .join("|")
}
